import data from "@emoji-mart/data";
import { Picker } from "emoji-mart";
import { useCallback, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";

interface EmojiMartEmoji {
  id: string;
  native?: string;
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

/**
 * Emoji picker using emoji-mart's web component.
 * Manages the picker imperatively to avoid React lifecycle issues.
 */
export function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<InstanceType<typeof Picker> | null>(null);
  const onSelectRef = useRef(onSelect);

  onSelectRef.current = onSelect;

  const handleSelect = useCallback((emoji: EmojiMartEmoji) => {
    if (emoji.native) {
      onSelectRef.current(emoji.native);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const picker = new Picker({
      data,
      onEmojiSelect: handleSelect,
      theme: 'light',
      previewPosition: 'none',
      skinTonePosition: 'search',
      set: 'native',
      maxFrequentRows: 2,
      navPosition: 'bottom',
      perLine: 8,
      parent: container,
      autoFocus: !isMobile,
    });
    pickerRef.current = picker;

    // Inject style to clean up the picker appearance
    requestAnimationFrame(() => {
      const shadowRoot = (container.firstChild as HTMLElement)?.shadowRoot;
      if (shadowRoot) {
        const style = document.createElement('style');
        style.textContent = `
          .sticky { backdrop-filter: none !important; -webkit-backdrop-filter: none !important; background-color: var(--em-color-background) !important; }
          input { font-size: 16px !important; }
          #nav button { color: rgba(var(--em-rgb-color), .85) !important; }
          #nav button[aria-selected] { color: rgb(var(--em-rgb-accent)) !important; }
        `;
        shadowRoot.appendChild(style);
      }
    });

    return () => {
      pickerRef.current = null;
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [handleSelect, isMobile]);

  return (
    <div
      ref={containerRef}
      className="emoji-mart-wrapper"
      style={{ isolation: 'isolate' }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    />
  );
}
